<app-header-latest></app-header-latest>

<!-- ── SECTION 1: HERO ─────────────────────────────────────── -->
<section class="ent-hero">
  <div class="container-wrapper">
    <div>
      <div class="section-eyebrow" style="background:rgba(255,255,255,0.08);color:rgba(180,210,255,0.9);border-color:rgba(59,130,246,0.25);">🏢 ENTERPRISE STACK</div>
      <h1>Enterprise HR<br>Built for <span class="gradient-text-vivid">High Scale</span></h1>
      <p>Secure, compliant, and highly configurable employee software engineered to power global organizations with 1000+ employees.</p>
      <div style="display:flex;gap:16px;">
        <a href="#" class="btn btn-primary" style="background:#3B82F6;">Contact Sales</a>
        <a href="#" class="btn btn-outline" style="color:#fff;border-color:rgba(255,255,255,0.3);">Security Overview</a>
      </div>
      <div class="sec-badges">
        <div class="badge-card"><div class="badge-card-icon">🛡️</div><div class="badge-card-title">SOC2 Type II</div><div class="badge-card-status">Certified</div></div>
        <div class="badge-card"><div class="badge-card-icon">🔒</div><div class="badge-card-title">ISO 27001</div><div class="badge-card-status">Compliant</div></div>
        <div class="badge-card"><div class="badge-card-icon">🔐</div><div class="badge-card-title">SAML SSO</div><div class="badge-card-status">Ready</div></div>
      </div>
    </div>
    <div>
      <div class="arch-widget">
        <div class="arch-layer active">API Gateway & WAF Firewall</div>
        <div class="arch-layer">Core Application Microservices
          <div class="arch-grid-cells">
            <div class="arch-cell">Core HR</div>
            <div class="arch-cell">Payroll Engine</div>
            <div class="arch-cell">BI Analytics</div>
          </div>
        </div>
        <div class="arch-layer active">Encrypted Multi-Entity Databases (AES-256)</div>
      </div>
    </div>
  </div>
</section>

<!-- ── SECTION 2: SLA ──────────────────────────────────────── -->
<section class="ent-sla-section">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">📋 SLA & Assurances</span>
      <h2 class="sec-h2">High-Grade Operations,<br><span class="gradient-text">Guaranteed.</span></h2>
      <p class="sec-sub">We match enterprise demands with dedicated resources, robust service agreements, and premier support.</p>
    </div>
    <div class="sla-grid">
      <div class="sla-card"><div class="sla-card-num">99.99%</div><div class="sla-card-title">Uptime SLA Guarantee</div><div class="sla-card-desc">Redundant database clusters and hot-failover setups mean your human resources portal is always online when your workforce needs it.</div></div>
      <div class="sla-card"><div class="sla-card-num">15m</div><div class="sla-card-title">Response Time Support SLA</div><div class="sla-card-desc">Dedicated account handlers and immediate escalations for critical tickets. Priority phone, Slack, and ticketing endpoints are open 24/7/365.</div></div>
    </div>
  </div>
</section>

<!-- ── SECTION 3: SECURITY ─────────────────────────────────── -->
<section class="ent-security">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">🔐 Security Architecture</span>
      <h2 class="sec-h2">Enterprise-Grade<br><span class="gradient-text">Security at Every Layer</span></h2>
      <p class="sec-sub">From encryption at rest to role-based access control — every aspect of NeoHRMZ is built with enterprise security standards.</p>
    </div>
    <div class="ent-sec-grid">
      <div class="ent-sec-card"><div class="ent-sec-card__icon">🔒</div><div class="ent-sec-card__title">AES-256 Encryption</div><div class="ent-sec-card__desc">All employee data is encrypted at rest using AES-256 and in transit using TLS 1.3. Zero plaintext storage, ever.</div><span class="ent-sec-card__badge">Always-On</span></div>
      <div class="ent-sec-card"><div class="ent-sec-card__icon">🛡️</div><div class="ent-sec-card__title">Multi-Tenant Isolation</div><div class="ent-sec-card__desc">Complete logical and physical data partition between tenants. No shared tables, no cross-tenant query risk. Audited quarterly.</div><span class="ent-sec-card__badge">SOC2 Verified</span></div>
      <div class="ent-sec-card"><div class="ent-sec-card__icon">👤</div><div class="ent-sec-card__title">SAML / OIDC SSO</div><div class="ent-sec-card__desc">Connect to Okta, Azure AD, Google Workspace, or any SAML-compliant IdP. Supports Just-In-Time provisioning and SCIM syncing.</div><span class="ent-sec-card__badge">Zero-Config</span></div>
    </div>
  </div>
</section>

<!-- ── SECTION 4: DEPLOYMENT ───────────────────────────────── -->
<section class="ent-deploy">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">🏗️ Deployment Options</span>
      <h2 class="sec-h2">Deploy Your Way,<br><span class="gradient-text">On Your Terms</span></h2>
      <p class="sec-sub">Whether you need multi-region cloud or on-premise, NeoHRMZ fits your infrastructure requirements.</p>
    </div>
    <div class="deploy-options">
      <div class="deploy-card">
        <span class="deploy-card__badge">Most Popular</span>
        <div class="deploy-card__title">Cloud SaaS</div>
        <div class="deploy-card__desc">Fully managed, auto-scaled, multi-region cloud deployment. Zero infrastructure management, instant updates, and global CDN delivery.</div>
        <div class="deploy-card__features">
          <div class="deploy-card__feat">Auto-scaling infrastructure</div>
          <div class="deploy-card__feat">Daily automated backups</div>
          <div class="deploy-card__feat">Instant feature updates</div>
          <div class="deploy-card__feat">99.99% uptime SLA included</div>
        </div>
      </div>
      <div class="deploy-card deploy-card--featured">
        <span class="deploy-card__badge">Enterprise Only</span>
        <div class="deploy-card__title">Private Cloud / On-Premise</div>
        <div class="deploy-card__desc">Full deployment within your own AWS, Azure, or GCP account. Or behind your firewall with our containerized stack — full data sovereignty guaranteed.</div>
        <div class="deploy-card__features">
          <div class="deploy-card__feat">Your VPC, your data</div>
          <div class="deploy-card__feat">Custom disaster recovery SLAs</div>
          <div class="deploy-card__feat">Dedicated support engineer</div>
          <div class="deploy-card__feat">HIPAA & FedRAMP options</div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── SECTION 5: CTA ──────────────────────────────────────── -->
<section class="ent-cta">
  <div class="container-wrapper">
    <h2 class="ent-cta__h2">Your Enterprise Deserves<br>Better HR Software.</h2>
    <p class="ent-cta__p">Book a personalized enterprise demo with our solutions team. Setup takes 4-6 weeks with dedicated implementation support.</p>
    <div class="ent-cta__actions">
      <a href="contact.html" class="btn btn-primary" style="background:#3B82F6;">Contact Enterprise Sales</a>
      <a href="#" class="btn btn-outline" style="color:#fff;border-color:rgba(255,255,255,0.3);">Download Security Whitepaper</a>
    </div>
  </div>
</section>

<app-peopleflow-footer></app-peopleflow-footer>

<!-- </body>
</html> -->
